import { EffectsMap } from 'use-effect-reducer'
import { trackUserEvent } from '@vend/utilities'
import { connectToTerminal } from '../../helpers/connectToTerminal'
import {
  getPaymentErrorMessage,
  isCancellation,
} from '../../helpers/errorChecking'
import {
  FAILED_PERSISTING_CAPTURED_PAYMENT,
  FAILED_CAPTURE,
  FAILED_INTENT,
  FAILED_TERMINAL_CONNECTION,
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  LSPAY_STEPS,
  STRIPE_ERROR_CODE,
  SWIPER_ERROR_CODES,
  LOGGING_EXCLUDED_STRIPE_ERRORS,
} from '../../helpers/lspayConstants'
import {
  capturePayment,
  collectPayment,
  createPaymentIntent,
  extractFromSecret,
  getPaymentMethodType,
  hasPaymentGoneThrough,
} from '../../helpers/paymentIntent'
import { WEB_REGISTER } from '../../../../web-register.constant'
import { getConnectionErrorMessage } from '../../helpers/getConnectionErrorMessage'
import { createCapturedTransaction } from '../../helpers/createCapturedTransaction'
import {
  PaymentEffect,
  PaymentEffectsMapArgs,
  PaymentEvent,
  PaymentState,
} from './types'

// Get the right effect for the given state
// This is quite repetitive with the reducer but the effect data comes from the state rather than from the event.
// May be improved later on ?
export const getEffectForRetry = (
  state: PaymentState
): PaymentEffect | undefined => {
  switch (state.status) {
    case 'connectingToTerminal': {
      return {
        type: 'connectTerminalEffect',
        pairingSN: state.pairing.sn,
        isSimulated: state.pairing.isSimulated,
        terminalID: state.pairing.terminalID,
        terminalModel: state.pairing.terminalModel,
      }
    }
    case 'creatingIntent': {
      return { type: 'createIntentEffect', terminal: state.terminal }
    }
    case 'collecting': {
      return {
        type: 'collectEffect',
        terminal: state.terminal,
        terminalMonitoringInstance: state.terminalMonitoringInstance,
        clientSecret: state.clientSecret,
        isRetry: true,
      }
    }
  }
}

export const getPaymentEffectsMap = ({
  outletId,
  register,
  amount,
  currency,
  retailerPaymentType,
  transactionId,
  registerSaleId,
  trackError,
  trackTiming,
  setDisableCNP,
  setIntentClientSecret,
  setSwiperError,
  isSandbox,
}: PaymentEffectsMapArgs): EffectsMap<
  PaymentState,
  PaymentEvent,
  PaymentEffect
> => {
  const { id: registerId, name: registerName } = register
  return {
    connectTerminalEffect: (state, effect, dispatch) => {
      connectToTerminal(
        {
          outletId,
          registerId,
          pairingSN: effect.pairingSN,
          isSimulated: effect.isSimulated,
          terminalID: effect.terminalID,
          terminalModel: effect.terminalModel,
        },
        isSandbox,
        trackTiming
      )
        .then(({ terminal, terminalMonitoringInstance }) => {
          dispatch({
            type: 'TERMINAL_OK',
            terminal,
            terminalMonitoringInstance,
          })
        })
        .catch(error => {
          if (state.status === 'connectingToTerminal') {
            dispatch({
              type: 'ERROR',
              errorMessage: getConnectionErrorMessage({
                error,
                pairingLabel: state.pairing.label || 'no label',
                paymentTypeName: state.retailerPaymentTypeName,
              }),
            })
          }
          trackError(FAILED_TERMINAL_CONNECTION, error, undefined, [
            STRIPE_ERROR_CODE.READER_ERROR,
          ])
        })
    },
    createIntentEffect: (_state, effect, dispatch) => {
      const { terminal } = effect
      const reader = terminal.getConnectedReader()

      createPaymentIntent({
        amount,
        currency,
        outletId,
        registerId,
        retailerPaymentTypeId: retailerPaymentType.id,
        metadata: {
          order_id: transactionId,
          terminal_label: reader.label || '',
          terminal_serial_number: reader.serial_number || '',
          register_name: registerName,
          register_sale_id: registerSaleId,
        },
      })
        .then(clientSecret => {
          // store intent in modal state for potential reuse at CNP flow
          setIntentClientSecret(clientSecret)
          dispatch({ type: 'INTENT_OK', clientSecret })
        })
        .catch(error => {
          if (error?.code === SWIPER_ERROR_CODES.LSPAY_AMOUNT_TOO_SMALL) {
            setSwiperError(error)
            return
          }

          trackError(FAILED_INTENT, error)

          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
          })
        })
    },
    collectEffect: (_state, effect, dispatch) => {
      const { terminal, terminalMonitoringInstance, isRetry } = effect
      collectPayment(
        effect.clientSecret,
        outletId,
        terminal,
        terminalMonitoringInstance,
        trackTiming,
        isRetry
      )
        .then(intent => {
          dispatch({ type: 'COLLECT_OK', intent })
          // extra success UBE as we debug issues where some payments are processed and automatically captured,
          // but never reach Swiper, and have no matching Raygun error.
          // We find those by reconciling payments with fees versus payments in our DB, amongst other cases with different issues.
          // See https://redash.service.us-west-2.aws.vendhq.com/queries/7021/source#18501
          trackUserEvent(
            WEB_REGISTER.DISPLAY_NAME,
            'LSPAY payment collectEffect OK',
            {
              transactionId,
              intent: intent.id,
              status: intent.status,
            }
          )
        })
        .catch(error => {
          dispatch({
            type: 'ERROR',
            errorMessage: getPaymentErrorMessage(error),
          })
          // extra error UBE as we debug issues where some payments are processed and automatically captured,
          // but never reach Swiper, and have no matching Raygun error.
          // We find those by reconciling payments with fees versus payments in our DB, amongst other cases with different issues.
          // See https://redash.service.us-west-2.aws.vendhq.com/queries/7021/source#18501
          trackUserEvent(
            WEB_REGISTER.DISPLAY_NAME,
            'LSPAY payment collectEffect error',
            {
              transactionId,
              intent: extractFromSecret(effect.clientSecret),
              intentStatus: error?.payment_intent?.status ?? '',
              msg: error?.message ?? '',
              code: error?.code ?? '',
              decline_code: error?.decline_code ?? '',
            }
          )

          // if its a cancellation send a UBE
          if (isCancellation(error)) {
            trackUserEvent(
              WEB_REGISTER.DISPLAY_NAME,
              'LSPAY payment collection cancelled',
              {
                error,
                transactionId,
              }
            )
            return
          }
          if (
            error?.code &&
            LOGGING_EXCLUDED_STRIPE_ERRORS.includes(error.code)
          ) {
            // if the error code is in the list of excluded codes, we don't want to log it
            return
          }

          trackError(
            `[lspay] collect or process error (${
              error?.code ?? 'no error code'
            })`,
            error,
            {
              transactionId,
              // the currency will help us tell if the payment might have been automatically captured
              // as AUD will be set to automatic capture to handle EFPOS payment
              currency,
              // keep track of the intent so we can check whether the payment went through or not, when debugging
              intent: extractFromSecret(effect.clientSecret),
            }
          )
        })
    },
    captureEffect: (_state, effect, dispatch) => {
      // Disallow user from switching to card not present LSPAY option
      // as terminal payment is about to capture
      setDisableCNP(true)
      const { intent } = effect
      const stopCaptureTimer = trackTiming(LSPAY_STEPS.CAPTURE)

      capturePayment({
        registerId,
        retailerPaymentTypeId: retailerPaymentType.id,
        transactionId,
        data: {
          amount,
          currency,
          lspay_intent_id: intent.id!,
          outlet_id: outletId,
          lspay_payment_method: getPaymentMethodType(intent),
        },
      })
        .then(transaction => {
          dispatch({
            type: 'CAPTURE_OK',
            transaction,
          })
        })
        .catch(error => {
          if (hasPaymentGoneThrough(intent.status)) {
            dispatch({
              type: 'CAPTURE_OK',
              // if capture method is already automatically captured because the currency is AUD or the customer is using interac card,
              // we know that the payment will have been processed, even if the request to persist the payment at Swiper fails
              // so we can dispatch a CAPTURE_OK event and resolve the current payment with a locally created transaction
              transaction: createCapturedTransaction({
                intent,
                registerId,
                transactionId,
                mainTenderAmount: amount,
                currency,
                retailerPaymentTypeId: retailerPaymentType.id,
                paymentTypeId: retailerPaymentType.type_id,
              }),
            })
            // track the error
            trackError(FAILED_PERSISTING_CAPTURED_PAYMENT, error, {
              intent,
            })
            return
          }

          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
          })
          // track the error
          trackError(FAILED_CAPTURE, error, { intent })
        })
        .finally(() => stopCaptureTimer())
    },
  }
}
